import { startTransition, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  PushMeldingApi,
  PushMeldingInputs,
  PushMeldingPayload,
  PushMeldingPriorityApi,
} from '@/components/dialogs/push-melding/type';
import { useGetQuery } from '@/queries/useGetQuery';
import { usePostQuery } from '@/queries/usePostQuery';
import { usePutQuery } from '@/queries/usePutQuery';
import { bmsApiResponseHandler, responseToToastHandler } from '@/utils/bmsApiResponseHandlers';
import { constructZuluTimeString, formatIsoDate, formatTime } from '@/utils/dateUtils';
import { isSuccesResponse, MeldingResp } from '@/utils/succesApiResponseHandler';

const toStatusCode = (resp: MeldingResp | null): string => {
  if (!resp) return 'TECHNISCHE_FOUT';

  if (typeof resp === 'string') {
    return resp;
  }

  if (typeof resp.status === 'string') {
    return resp.status;
  }

  if (typeof resp.id === 'number') {
    return 'SUCCES';
  }

  return 'TECHNISCHE_FOUT';
};

const mapPriorityToApi = (priority: PushMeldingInputs['priority']): PushMeldingPriorityApi =>
  priority === 'warning' ? 'WARNING' : 'INFO';

const mapPriorityToUi = (priority: PushMeldingPriorityApi): PushMeldingInputs['priority'] =>
  priority === 'WARNING' ? 'warning' : 'info';

const getStartDateTimeFromForm = (values: PushMeldingInputs): string | null => {
  if (!values.startDatum || !values.startTijd) {
    return null;
  }

  return constructZuluTimeString(values.startDatum, values.startTijd);
};

const buildEndDateTime = (values: PushMeldingInputs): string | null => {
  if (!values.eindDatum || !values.eindTijd) {
    return null;
  }

  return constructZuluTimeString(values.eindDatum, values.eindTijd);
};

const usePushMelding = (_details: unknown, handleOpenChange: (open: boolean) => void) => {
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [currentApiData, setCurrentApiData] = useState<PushMeldingApi | null>(null);

  const { handleSubmit, reset, control, setValue, watch, getValues } = useForm<PushMeldingInputs>({
    mode: 'onSubmit',
    defaultValues: {
      meldingTekst: '',
      priority: 'info',
      startDatum: '',
      startTijd: '',
      eindDatum: '',
      eindTijd: '',
    },
  });

  const mapActive = useCallback((raw: PushMeldingApi): PushMeldingApi | null => raw ?? null, []);
  const { data, fetchData, hasBeenCalled, clearData } = useGetQuery<PushMeldingApi | null>(mapActive);

  useEffect(() => {
    startTransition(() => {
      void fetchData('/push-meldingen/');
    });
  }, [fetchData]);

  useEffect(() => {
    if (!hasBeenCalled) return;
    if (data && data.id) {
      setCurrentId(data.id);
      setCurrentApiData(data);

      const start = data.startDateTime ? new Date(data.startDateTime) : undefined;
      const end = data.endDateTime ? new Date(data.endDateTime) : undefined;

      reset({
        startDatum: start ? formatIsoDate(start) : '',
        startTijd: start ? formatTime(start) : '',
        eindDatum: end ? formatIsoDate(end) : '',
        eindTijd: end ? formatTime(end) : '',
        priority: mapPriorityToUi(data.priority),
        meldingTekst: data.message ?? '',
      });
    } else {
      setCurrentId(null);
      setCurrentApiData(null);

      reset({
        startDatum: '',
        startTijd: '',
        eindDatum: '',
        eindTijd: '',
        priority: 'info',
        meldingTekst: '',
      });
    }
  }, [hasBeenCalled, data, reset]);

  const resetDialog = (closeButtonClick: boolean) => {
    if (closeButtonClick) {
      handleOpenChange(false);
    }

    setCurrentId(null);
    setCurrentApiData(null);

    reset({
      startDatum: '',
      startTijd: '',
      eindDatum: '',
      eindTijd: '',
      priority: 'info',
      meldingTekst: '',
    });

    clearData();
  };

  const { post } = usePostQuery<PushMeldingPayload, MeldingResp>('/push-meldingen/aanmaken');
  const { put } = usePutQuery<PushMeldingPayload, MeldingResp>();

  const makePayloadFromValues = (values: PushMeldingInputs, isCreate: boolean): PushMeldingPayload => {
    const message = (values.meldingTekst ?? '').trim();
    const nowIso = new Date().toISOString();

    const startFromForm = getStartDateTimeFromForm(values);

    const startDateTime = isCreate
      ? (startFromForm ?? nowIso)
      : (startFromForm ?? currentApiData?.startDateTime ?? nowIso);

    const basePayload = {
      message,
      priority: mapPriorityToApi(values.priority),
      startDateTime,
      endDateTime: buildEndDateTime(values),
    };

    return isCreate ? basePayload : { id: currentId!, ...basePayload };
  };

  const onSubmit = async (values: PushMeldingInputs) => {
    const hasId = !!currentId;
    const isCreate = !hasId;
    const payload = makePayloadFromValues(values, isCreate);

    try {
      const resp = hasId ? await put(`/push-meldingen/${currentId}`, payload) : await post(payload);
      const status = toStatusCode(resp);

      const messageKey = hasId ? 'push-melding-bijgewerkt' : 'push-melding-aangemaakt';
      const toast = bmsApiResponseHandler(status, messageKey);
      responseToToastHandler(toast);

      if (isSuccesResponse(resp)) {
        resetDialog(true);
      }
    } catch (error) {
      responseToToastHandler({
        type: 'error',
        message: 'Onverwachte fout: Probeer de melding opnieuw op te slaan.',
      });
      console.error({ error });
    }
  };

  const deleteMelding = async () => {
    if (!currentId) {
      resetDialog(true);
      return;
    }

    const values = getValues();
    const nowIso = new Date().toISOString();

    const message = (values.meldingTekst ?? '').trim() || (currentApiData?.message ?? '');
    const priority = currentApiData?.priority ?? mapPriorityToApi(values.priority);

    const startFromForm = getStartDateTimeFromForm(values);
    const startDateTime = currentApiData?.startDateTime ?? startFromForm ?? nowIso;

    const payload: PushMeldingPayload = {
      id: currentId,
      message,
      priority,
      startDateTime,
      endDateTime: nowIso,
    };

    try {
      const resp = await put(`/push-meldingen/${payload.id}`, payload);
      const status = toStatusCode(resp);

      const toast = bmsApiResponseHandler(status, 'push-melding-verwijderd');
      responseToToastHandler(toast);

      if (isSuccesResponse(resp)) {
        resetDialog(true);
      }
    } catch (error) {
      responseToToastHandler({
        type: 'error',
        message: 'Onverwachte fout: Probeer de melding opnieuw te verwijderen.',
      });
      console.error({ error });
    }
  };

  return {
    control,
    handleSubmit,
    onSubmit,
    resetDialog,
    setValue,
    watch,
    id: currentId,
    deleteMelding,
  };
};

export default usePushMelding;
